const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const normalized = value.trim().replace(",", ".");
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const round = (value, decimals = 2) => {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

export const formatNumber = (value, decimals = 2) =>
  new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: 0,
  }).format(value);

export function calculateMedicationDose({
  medication,
  regimen,
  weightKg,
  ageValue,
  ageUnit = "years",
  concentrationMgPerMl,
  doseMgKg,
  dosesPerDay,
  durationDays,
  maxSingleMg,
  maxDailyMg,
  manualCalculation = "mgKgDose",
  manualFixedDoseMg,
}) {
  const errors = [];
  const warnings = [];
  const weight = toNumber(weightKg);
  const age = toNumber(ageValue);
  const concentration = toNumber(concentrationMgPerMl);
  const dose = toNumber(doseMgKg);
  const dailyDoses = toNumber(dosesPerDay);
  const duration = toNumber(durationDays);
  const singleLimit = toNumber(maxSingleMg);
  const dailyLimit = toNumber(maxDailyMg);

  if (!medication) errors.push("Selecione um medicamento.");
  if (!regimen) errors.push("Selecione uma indicação/regime.");
  if (!weight || weight <= 0) errors.push("Informe um peso válido em kg.");
  if (age !== null && age < 0) errors.push("Informe uma idade válida.");

  const ageYears =
    age === null ? null : ageUnit === "days" ? age / 365 : ageUnit === "months" ? age / 12 : age;

  if (
    medication?.restrictions?.minAgeYears !== undefined &&
    ageYears !== null &&
    ageYears < medication.restrictions.minAgeYears
  ) {
    errors.push(`Uso restrito a pacientes com ${medication.restrictions.minAgeYears} anos ou mais.`);
  }

  const calculation = regimen?.calculation === "manual" ? manualCalculation : regimen?.calculation;
  const effectiveDose = dose ?? regimen?.doseMgKg ?? null;
  const effectiveDosesPerDay = dailyDoses ?? regimen?.dosesPerDay ?? null;
  const effectiveDuration = duration ?? regimen?.durationDays ?? null;
  const effectiveSingleLimit = singleLimit ?? regimen?.maxSingleMg ?? null;
  const effectiveDailyLimit = dailyLimit ?? regimen?.maxDailyMg ?? null;

  if (["mgKgDose", "mgKgDay"].includes(calculation) && (!effectiveDose || effectiveDose <= 0)) {
    errors.push("Informe uma dose válida em mg/kg.");
  }
  if (calculation === "mgKgDay" && (!effectiveDosesPerDay || effectiveDosesPerDay <= 0)) {
    errors.push("Informe quantas doses serão administradas por dia.");
  }
  if (calculation === "fixedMg" && (!toNumber(manualFixedDoseMg) || toNumber(manualFixedDoseMg) <= 0)) {
    errors.push("Informe uma dose fixa válida em mg.");
  }

  if (errors.length) return { valid: false, errors, warnings };

  let calculatedSingleMg;
  let calculatedDailyMg;

  if (calculation === "mgKgDay") {
    calculatedDailyMg = weight * effectiveDose;
    calculatedSingleMg = calculatedDailyMg / effectiveDosesPerDay;
  } else if (calculation === "fixedMg") {
    calculatedSingleMg = toNumber(manualFixedDoseMg);
    calculatedDailyMg = calculatedSingleMg * (effectiveDosesPerDay || 1);
  } else {
    calculatedSingleMg = weight * effectiveDose;
    calculatedDailyMg = calculatedSingleMg * (effectiveDosesPerDay || 1);
  }

  let finalSingleMg = calculatedSingleMg;
  let finalDailyMg = calculatedDailyMg;
  let limited = false;

  if (effectiveSingleLimit && finalSingleMg > effectiveSingleLimit) {
    finalSingleMg = effectiveSingleLimit;
    finalDailyMg = finalSingleMg * (effectiveDosesPerDay || 1);
    limited = true;
    warnings.push(`Dose limitada ao máximo de ${formatNumber(effectiveSingleLimit)} mg por tomada.`);
  }

  const weightDailyLimit = regimen?.maxDailyMgKg ? weight * regimen.maxDailyMgKg : null;
  const resolvedDailyLimit =
    effectiveDailyLimit && weightDailyLimit
      ? Math.min(effectiveDailyLimit, weightDailyLimit)
      : effectiveDailyLimit || weightDailyLimit;

  if (resolvedDailyLimit && finalDailyMg > resolvedDailyLimit) {
    finalDailyMg = resolvedDailyLimit;
    finalSingleMg = finalDailyMg / (effectiveDosesPerDay || 1);
    limited = true;
    warnings.push(`Dose limitada ao máximo diário de ${formatNumber(resolvedDailyLimit)} mg.`);
  }

  const volumeMl = concentration && concentration > 0 ? finalSingleMg / concentration : null;
  const drops =
    volumeMl && medication?.dropsPerMl ? Math.round(volumeMl * medication.dropsPerMl) : null;
  const totalVolumeMl =
    volumeMl && effectiveDosesPerDay && effectiveDuration
      ? volumeMl * effectiveDosesPerDay * effectiveDuration
      : null;

  return {
    valid: true,
    errors,
    warnings,
    calculation,
    calculatedSingleMg: round(calculatedSingleMg),
    finalSingleMg: round(finalSingleMg),
    calculatedDailyMg: round(calculatedDailyMg),
    finalDailyMg: round(finalDailyMg),
    volumeMl: volumeMl ? round(volumeMl) : null,
    drops,
    totalVolumeMl: totalVolumeMl ? round(totalVolumeMl) : null,
    dosesPerDay: effectiveDosesPerDay || 1,
    durationDays: effectiveDuration,
    intervalHours:
      regimen?.intervalHours || (effectiveDosesPerDay ? round(24 / effectiveDosesPerDay, 1) : null),
    concentrationMgPerMl: concentration,
    doseMgKg: effectiveDose,
    limited,
  };
}

export function buildPrescriptionText({ medication, regimen, result, patientName, professional }) {
  if (!result?.valid || !medication || !regimen) return "";

  const amount = [
    `${formatNumber(result.finalSingleMg)} mg`,
    result.volumeMl ? `(${formatNumber(result.volumeMl)} mL)` : null,
    result.drops ? `ou aproximadamente ${result.drops} gotas` : null,
  ]
    .filter(Boolean)
    .join(" ");

  const frequency = result.intervalHours
    ? `a cada ${formatNumber(result.intervalHours, 1)} horas`
    : `${result.dosesPerDay} vez(es) ao dia`;
  const duration = result.durationDays ? `, por ${formatNumber(result.durationDays, 1)} dia(s)` : "";

  return [
    patientName ? `Paciente: ${patientName}` : null,
    `${medication.name} - ${medication.presentation}`,
    `Administrar ${amount}, ${regimen.route || medication.route}, ${frequency}${duration}.`,
    result.totalVolumeMl
      ? `Quantidade total estimada: ${formatNumber(result.totalVolumeMl)} mL.`
      : null,
    professional?.name ? `Profissional: ${professional.name} - CRO ${professional.cro || "não informado"}` : null,
  ]
    .filter(Boolean)
    .join("\n");
}
